
import { Users, Search, Phone, Mail, Loader2, Edit, Trash2 } from "lucide-react"
import { useState, useEffect } from "react"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Layout } from "@/components/Layout"
import { useProfiles } from "@/hooks/useProfiles"
import NovoMoradorModal from "@/components/NovoMoradorModal"
import EditarMoradorModal from "@/components/EditarMoradorModal"
import { useAuth } from "@/hooks/useAuth"
import { useCondominium } from "@/hooks/useCondominium"
import { Badge } from "@/components/ui/badge"
import { useUnits } from "@/hooks/useUnits"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { useQueryClient } from "@tanstack/react-query"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

const Moradores = () => {
  const { data: moradores, isLoading, error } = useProfiles()
  const { profile, loading: authLoading } = useAuth()
  const { data: condominium } = useCondominium()
  const [searchTerm, setSearchTerm] = useState("")
  const [editModal, setEditModal] = useState<{ open: boolean; morador?: any }>({ open: false })
  const { toast } = useToast()
  const queryClient = useQueryClient()

  useEffect(() => {
    console.log('Página Moradores - estado atual:', {
      authLoading,
      profile: profile ? {
        id: profile.id,
        role: profile.role,
        condominium_id: profile.condominium_id,
        full_name: profile.full_name
      } : null,
      moradores: moradores?.length,
      isLoading,
      error: error?.message
    })
  }, [moradores, isLoading, error, profile, authLoading])

  // Verificar se pode criar novos moradores (porteiros não podem)
  const canCreateMorador = profile?.role !== 'morador' && profile?.role !== 'porteiro'
  const canEditDelete = profile?.role === 'sindico' || profile?.role === 'administracao' || profile?.role === 'superadmin'

  const handleDeleteUser = async (userId: string, userName: string) => {
    try {
      console.log('Deletando usuário:', userId)
      
      // Primeiro inativar o perfil
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ is_active: false })
        .eq('id', userId)

      if (profileError) {
        throw profileError
      }

      toast({
        title: "Sucesso!",
        description: `${userName} foi removido com sucesso.`,
      })

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ["profiles"] })
    } catch (error: any) {
      console.error('Erro ao deletar usuário:', error)
      toast({
        title: "Erro",
        description: "Erro ao remover usuário: " + error.message,
        variant: "destructive",
      })
    }
  }

  const filteredMoradores = moradores?.filter(morador => {
    // Filtrar apenas moradores (não colaboradores)
    if (morador.role !== 'morador') return false
    
    const searchLower = searchTerm.toLowerCase()
    const fullName = morador.full_name || ""
    const email = morador.email || ""
    const phone = morador.phone || ""
    const apartment = morador.apartment_number || ""
    
    return fullName.toLowerCase().includes(searchLower) ||
           email.toLowerCase().includes(searchLower) ||
           phone.includes(searchTerm) ||
           apartment.includes(searchTerm)
  }) || []

  if (authLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-gray-600">Carregando usuário...</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (!profile) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center text-red-600">
            Erro: Usuário não autenticado. Faça login novamente.
          </div>
        </div>
      </Layout>
    )
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-gray-600">Carregando moradores...</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center text-red-600">
            Erro ao carregar moradores: {error.message}
            <br />
            <small className="text-gray-500">
              Usuário: {profile.full_name} ({profile.role})
              {condominium && ` - Condomínio: ${condominium.name}`}
            </small>
          </div>
        </div>
      </Layout>
    )
  }

  const getInitials = (name: string | null) => {
    if (!name) return "?"
    return name.split(' ').map(n => n[0]).join('').toUpperCase()
  }

  const getRoleLabel = (role: string) => {
    const roles = {
      morador: "Morador",
      porteiro: "Porteiro", 
      sindico: "Síndico",
      administracao: "Administração",
      superadmin: "Super Admin"
    }
    return roles[role as keyof typeof roles] || role
  }

  console.log('Renderizando página com moradores filtrados:', filteredMoradores.length)

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-ita-black">Moradores</h1>
            <p className="text-gray-600">Gerencie os moradores do condomínio</p>
            <div className="text-sm text-gray-500 mt-2 space-y-1">
              <p>Usuário logado: {profile.full_name} ({getRoleLabel(profile.role)})</p>
              {condominium && (
                <p>Condomínio: {condominium.name}</p>
              )}
              <p>Total de moradores carregados: {moradores?.length || 0}</p>
            </div>
          </div>
          {canCreateMorador && <NovoMoradorModal />}
        </div>

        <div className="flex items-center space-x-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input 
              placeholder="Buscar moradores..." 
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {filteredMoradores.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {searchTerm ? "Nenhum morador encontrado para a busca" : "Nenhum morador encontrado"}
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Total de moradores no sistema: {moradores?.length || 0}
            </p>
            {profile.role === 'morador' && (
              <p className="text-xs text-orange-500 mt-2">
                Como morador, você pode ver apenas os moradores do seu condomínio
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMoradores.map((morador) => (
              <Card key={morador.id} className="ita-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Avatar>
                        <AvatarFallback className="bg-ita-blue text-white">
                          {getInitials(morador.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <CardTitle className="text-lg font-semibold">
                          {morador.full_name || "Nome não informado"}
                        </CardTitle>
                        <CardDescription className="space-y-1">
                          <div>
                            {morador.apartment_number && morador.building 
                              ? `${morador.building}-${morador.apartment_number}` 
                              : morador.apartment_number || "Sem unidade"
                            } • {getRoleLabel(morador.role)}
                          </div>
                          <Badge variant={
                            morador.resident_type === 'owner' ? 'default' :
                            morador.resident_type === 'tenant' ? 'secondary' :
                            'outline'
                          } className="text-xs">
                            {morador.resident_type === 'owner' ? '🏠 Proprietário' :
                             morador.resident_type === 'tenant' ? '🔑 Locatário' :
                             morador.resident_type === 'both' ? '🏠🔑 Ambos' :
                             '🏠 Proprietário'}
                          </Badge>
                        </CardDescription>
                      </div>
                    </div>
                    {canEditDelete && (
                      <div className="flex space-x-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0"
                          onClick={() => setEditModal({ open: true, morador })}
                        >
                          <Edit className="h-4 w-4 text-blue-500" />
                        </Button>
                        {morador.id !== profile.id && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmar remoção</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja remover {morador.full_name}? Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteUser(morador.id, morador.full_name || 'usuário')}
                                  className="bg-red-500 hover:bg-red-600"
                                >
                                  Remover
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {morador.phone && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Phone className="h-4 w-4 text-ita-blue" />
                      <span>{morador.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Mail className="h-4 w-4 text-ita-blue" />
                    <span>{morador.email}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {editModal.morador && (
          <EditarMoradorModal
            open={editModal.open}
            onOpenChange={(open) => setEditModal({ open, morador: open ? editModal.morador : undefined })}
            morador={editModal.morador}
          />
        )}
      </div>
    </Layout>
  )
}

export default Moradores
